import { useId } from "react"
import navConfig from "../../data/nav-config.json"
import { useNavScroll } from "../../hooks/use-nav-scroll"
import { LandingLogotye } from "./LandingLogotye"

export function LandingNav() {
  const navId = useId()
  const { activeSection, showNav, isInDemoSection, scrollToAnchor } = useNavScroll()

  // Calculate bracket logo opacity based solely on scroll position
  const getBracketLogoOpacity = () => {
    // Full brightness in demo section, dimmed in both Bracky and Bracket sections, default bright elsewhere
    if (isInDemoSection) return 1
    if (activeSection === "bracky" || activeSection === "bracket") return 0.33 // Keep consistent dimness
    return 1
  }

  const getBracketLogoHoverOpacity = () => {
    const baseOpacity = getBracketLogoOpacity()
    // Subtle hover effect: lighten slightly when dim (<0.5) or darken slightly when bright (>0.5)
    return baseOpacity > 0.5 ? baseOpacity - 0.1 : baseOpacity + 0.05
  }

  // Helper: scroll to top of page (logo click).
  const scrollToTop = () => {
    scrollToAnchor("hero")
  }

  const handleGetStarted = () => {
    // Open Farcaster miniapp in a new window
    window.open("https://bracky.app", "_blank", "noopener,noreferrer")
  }

  return (
    <>
      {/* Bracket Logotype (set opacity in const control) for lg+ screens that scrolls to top when clicked  */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Scroll to top"
        className="hidden xl:block fixed top-[2.62rem] -left-[4.9rem] z-511 transform scale-[.41] active:scale-[.40] transition-all duration-300 cursor-pointer select-none origin-center bg-transparent border-none p-0"
        style={
          {
            opacity: getBracketLogoOpacity(),
            "--hover-opacity": getBracketLogoHoverOpacity(),
          } as React.CSSProperties & { "--hover-opacity": number }
        }
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = getBracketLogoHoverOpacity().toString()
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = getBracketLogoOpacity().toString()
        }}
      >
        <LandingLogotye />
      </button>
      <div
        id={navId}
        className={`w-[90%] sm:w-[80%] max-w-[600px] mx-auto flex justify-center px-0 transition-all duration-300 transform-gpu ${
          showNav ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0 pointer-events-none"
        } md:translate-y-0 md:opacity-100 md:pointer-events-auto`}
      >
        {/* Nav container */}
        <div className="inline-flex w-full md:w-auto items-center justify-center md:justify-start gap-[0.3rem] sm:gap-2 bg-white/15 backdrop-blur-lg border border-white/10 rounded-[1.15rem] py-1.5 sm:py-[.55rem] px-4 md:pl-6 md:pr-4">
          {/* Navigation items with logo as first item */}
          {/* Bracket Logo - First navigation item */}
          <button
            type="button"
            onClick={scrollToTop}
            aria-label="Scroll to top"
            className={`group transition-colors duration-150 focus:outline-hidden  flex items-center h-[34px] sm:h-[38px] px-3 py-1 sm:px-4 sm:py-2 text-white rounded-[.3rem] skew-x-[-16deg] ${
              activeSection === "" ? "bg-white/20" : "hover:bg-white/11"
            } active:bg-white/30`}
          >
            <span className="inline-flex transform-[skewX(16deg)]">
              <img
                src={navConfig.logo.src}
                alt={navConfig.logo.alt}
                className="select-none h-5 sm:h-6 w-auto max-w-none"
                draggable="false"
                loading="lazy"
                decoding="async"
              />
            </span>
          </button>

          {navConfig.items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`group flex items-center h-[34px] sm:h-[38px] py-1 px-[.8rem] sm:py-2 sm:px-[.9rem] text-base font-medium duration-150 transition-all transform-gpu text-white rounded-[.3rem] skew-x-[-16deg] ${
                activeSection === item.id ? "bg-white/20" : "hover:bg-white/11 active:bg-white/30"
              }`}
              onClick={() => {
                scrollToAnchor(item.id)
              }}
            >
              <span className="inline-block -translate-y-[.05rem] skew-x-16">{item.label}</span>
            </button>
          ))}

          {/* Divider */}
          <span className="hidden md:block w-px h-6 bg-white/10 mx-1 skew-x-[-16deg]" />

          {/* Start trading CTA (visible on md and above) */}
          <button
            type="button"
            onClick={handleGetStarted}
            className="hidden md:inline-flex items-center justify-center h-[38px] px-5 text-base font-semibold whitespace-nowrap rounded-full transition-all duration-150 bg-[#001CFF] text-white hover:bg-[#001CFF]"
          >
            Start Now
          </button>
        </div>
      </div>
    </>
  )
}
